/**
 * Payslip OCR Service
 *
 * API service for processing payslip documents using Sarvam AI + Claude API.
 * Extracts structured data from payslips and maps to form fields.
 *
 * Base URL: /api/offer-calculation/ocr
 */

import axiosInstance from '../config/axios.config';

const BASE_URL = '/offer-calculation/ocr';

/**
 * Process a payslip document with OCR
 * @param {File} file - Payslip file to process (PDF, JPG, JPEG, PNG)
 * @param {Function} onProgress - Optional progress callback (0-100)
 * @param {string} provider - OCR provider ('textract' or 'sarvam'). Default: 'textract'
 * @returns {Promise} OCR result with extracted data and field mappings
 *
 * Response structure:
 * {
 *   success: true,
 *   message: 'Payslip processed successfully',
 *   data: {
 *     extracted_data: {
 *       employee_name: 'John Doe',
 *       net_salary: 85000,
 *       gross_salary: 100000,
 *       ...
 *     },
 *     field_mappings: {
 *       name: 'John Doe',
 *       net_salary: 85000,
 *       gross_salary: 100000,
 *       ...
 *     },
 *     confidence_scores: {
 *       employee_name: 0.95,
 *       net_salary: 0.92,
 *       ...
 *     },
 *     processing_time_ms: 4500,
 *     provider: 'textract'
 *   }
 * }
 */
export const processPayslip = async (file, onProgress = null, provider = 'textract', leadId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('provider', provider); // Add provider selection
    // Optional lead_id: when present, backend persists the file to S3 and
    // appends it to lead.payslip_paths so View Payslip works on edit reload.
    // Without lead_id, backend keeps the legacy temp-then-delete behavior.
    if (leadId) {
        formData.append('lead_id', String(leadId));
    }

    // Set timeout based on provider
    const timeout = provider === 'textract' ? 120000 : 300000; // Textract: 2min, Sarvam: 5min

    const config = {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: timeout,
    };

    // Add progress tracking if callback provided
    if (onProgress) {
        config.onUploadProgress = (progressEvent) => {
            // Upload is 30% of total progress
            const uploadPercent = Math.round((progressEvent.loaded * 30) / progressEvent.total);
            onProgress(uploadPercent);
        };
    }

    const response = await axiosInstance.post(
        `${BASE_URL}/payslip`,
        formData,
        config
    );
    return response.data;
};

/**
 * Process multiple payslip documents with OCR (up to 3 files)
 * @param {File[]} files - Array of payslip files (max 3)
 * @param {Function} onProgress - Optional progress callback (0-100)
 * @param {string} provider - OCR provider ('textract' or 'sarvam'). Default: 'textract'
 * @returns {Promise} Merged OCR result with averaged salary data
 */
export const processMultiplePayslips = async (files, onProgress = null, provider = 'textract', leadId = null) => {
    const formData = new FormData();
    files.forEach((file) => {
        formData.append('files[]', file);
    });
    formData.append('provider', provider);
    if (leadId) {
        formData.append('lead_id', String(leadId));
    }

    // Longer timeout for multiple files
    const baseTimeout = provider === 'textract' ? 120000 : 300000;
    const timeout = baseTimeout * files.length;

    const config = {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: timeout,
    };

    if (onProgress) {
        config.onUploadProgress = (progressEvent) => {
            const uploadPercent = Math.round((progressEvent.loaded * 30) / progressEvent.total);
            onProgress(uploadPercent);
        };
    }

    const response = await axiosInstance.post(
        `${BASE_URL}/payslips`,
        formData,
        config
    );
    return response.data;
};

/**
 * Get OCR service information (credits required, supported formats, providers)
 * @returns {Promise} Service info
 *
 * Response structure:
 * {
 *   success: true,
 *   data: {
 *     supported_formats: ['pdf', 'jpg', 'jpeg', 'png'],
 *     max_file_size_mb: 5,
 *     providers: {
 *       textract: {
 *         name: 'AWS Textract',
 *         credits_required: 2,
 *         estimated_time: '30-60 seconds',
 *         advantages: ['Fast processing', 'Native table detection', 'High accuracy'],
 *         recommended: true
 *       },
 *       sarvam: {
 *         name: 'Sarvam AI',
 *         credits_required: 2,
 *         estimated_time: '4-5 minutes',
 *         advantages: ['Indian language support', 'Polling-based', 'Backup option'],
 *         recommended: false
 *       }
 *     },
 *     default_provider: 'textract',
 *     features: {
 *       payslip_extraction: true,
 *       table_detection: true,
 *       multi_language: true,
 *       confidence_scores: true
 *     }
 *   }
 * }
 */
export const getServiceInfo = async () => {
    const response = await axiosInstance.get(`${BASE_URL}/service-info`);
    return response.data;
};

/**
 * Test OCR service connectivity (for debugging)
 * @returns {Promise} Connectivity test results
 */
export const testConnectivity = async () => {
    const response = await axiosInstance.get(`${BASE_URL}/test-connectivity`);
    return response.data;
};

/**
 * Parse date string to Date object
 * Handles various date formats from OCR extraction
 *
 * @param {string} dateStr - Date string in various formats (DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, etc.)
 * @returns {Date|null} Parsed Date object or null if invalid
 */
export const parseDateString = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;

    // Clean up the string
    const cleanStr = dateStr.trim();
    if (!cleanStr) return null;

    // Try different date formats
    let date = null;

    // Format: DD/MM/YYYY or DD-MM-YYYY
    const dmyMatch = cleanStr.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (dmyMatch) {
        const [, day, month, year] = dmyMatch;
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    // Format: YYYY-MM-DD or YYYY/MM/DD
    if (!date) {
        const ymdMatch = cleanStr.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
        if (ymdMatch) {
            const [, year, month, day] = ymdMatch;
            date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
    }

    // Format: DD Month YYYY or Month DD, YYYY (e.g., "15 January 1990" or "January 15, 1990")
    if (!date) {
        const months = {
            'jan': 0, 'january': 0, 'feb': 1, 'february': 1, 'mar': 2, 'march': 2,
            'apr': 3, 'april': 3, 'may': 4, 'jun': 5, 'june': 5, 'jul': 6, 'july': 6,
            'aug': 7, 'august': 7, 'sep': 8, 'september': 8, 'oct': 9, 'october': 9,
            'nov': 10, 'november': 10, 'dec': 11, 'december': 11
        };

        // DD Month YYYY
        const dmyTextMatch = cleanStr.match(/^(\d{1,2})\s+([a-z]+)\s+(\d{4})$/i);
        if (dmyTextMatch) {
            const [, day, monthText, year] = dmyTextMatch;
            const month = months[monthText.toLowerCase()];
            if (month !== undefined) {
                date = new Date(parseInt(year), month, parseInt(day));
            }
        }

        // Month DD, YYYY
        if (!date) {
            const mdyTextMatch = cleanStr.match(/^([a-z]+)\s+(\d{1,2}),?\s+(\d{4})$/i);
            if (mdyTextMatch) {
                const [, monthText, day, year] = mdyTextMatch;
                const month = months[monthText.toLowerCase()];
                if (month !== undefined) {
                    date = new Date(parseInt(year), month, parseInt(day));
                }
            }
        }
    }

    // Validate the date is reasonable (between 1900 and current year + 1)
    if (date && !isNaN(date.getTime())) {
        const year = date.getFullYear();
        if (year >= 1900 && year <= new Date().getFullYear() + 1) {
            return date;
        }
    }

    return null;
};

/**
 * Apply OCR extracted data to form data
 * Maps OCR field_mappings to the provided formData object
 *
 * @param {Object} formData - Current form data
 * @param {Object} ocrFieldMappings - OCR extracted field mappings
 * @param {Array} formTemplate - Form template fields (optional, for validation)
 * @returns {Object} Updated form data with OCR values applied
 */
export const applyOCRToFormData = (formData, ocrFieldMappings, formTemplate = []) => {
    if (!ocrFieldMappings || typeof ocrFieldMappings !== 'object') {
        return formData;
    }

    const updatedFormData = { ...formData };

    // Date fields that need to be converted to Date objects
    const dateFields = ['dob', 'date_of_birth', 'date_of_joining', 'date_of_retirement', 'gratuity_start_date'];

    // Apply each OCR field mapping
    Object.entries(ocrFieldMappings).forEach(([key, value]) => {
        // Skip null/undefined values
        if (value === null || value === undefined) return;

        // Skip zero values for numeric fields (might be "not found")
        if (typeof value === 'number' && value === 0) return;

        // Convert date strings to Date objects for date fields
        if (dateFields.includes(key) && typeof value === 'string') {
            const parsedDate = parseDateString(value);
            if (parsedDate) {
                updatedFormData[key] = parsedDate;
            }
            return;
        }

        // Apply the value to formData
        updatedFormData[key] = value;
    });

    return updatedFormData;
};

/**
 * Format OCR confidence as percentage
 * @param {number} confidence - Confidence score (0-1)
 * @returns {string} Formatted percentage (e.g., "95%")
 */
export const formatConfidence = (confidence) => {
    if (typeof confidence !== 'number') return 'N/A';
    return `${Math.round(confidence * 100)}%`;
};

/**
 * Get confidence badge severity based on score
 * @param {number} confidence - Confidence score (0-1)
 * @returns {string} PrimeReact badge severity
 */
export const getConfidenceSeverity = (confidence) => {
    if (typeof confidence !== 'number') return 'secondary';
    if (confidence >= 0.9) return 'success';
    if (confidence >= 0.7) return 'info';
    if (confidence >= 0.5) return 'warning';
    return 'danger';
};

/**
 * Validate file for OCR processing
 * @param {File} file - File to validate
 * @returns {Object} Validation result {valid: boolean, error?: string}
 */
export const validateFile = (file) => {
    if (!file) {
        return { valid: false, error: 'No file provided' };
    }

    const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png'
    ];

    const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png'];
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(extension)) {
        return {
            valid: false,
            error: 'Only PDF, JPG, JPEG, and PNG files are allowed'
        };
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
        return {
            valid: false,
            error: 'File size must not exceed 5MB'
        };
    }

    return { valid: true };
};

/**
 * Check if a document is a payslip based on document definition
 * @param {Object} documentDef - Document definition object
 * @returns {boolean} True if document is a payslip
 */
export const isPayslipDocument = (documentDef) => {
    if (!documentDef) return false;

    const payslipKeywords = [
        'payslip',
        'pay slip',
        'salary slip',
        'salary_slip',
        'payslip_document',
        'income_proof'
    ];

    const name = (documentDef.name || '').toLowerCase();
    const code = (documentDef.code || '').toLowerCase();
    const category = (documentDef.category || '').toLowerCase();

    return payslipKeywords.some(keyword =>
        name.includes(keyword) ||
        code.includes(keyword) ||
        category.includes(keyword)
    );
};

/**
 * Get human-readable field labels for OCR extracted data
 * @param {string} fieldKey - Field key from OCR extraction
 * @returns {string} Human-readable label
 */
export const getFieldLabel = (fieldKey) => {
    const labels = {
        // ============================================
        // EMPLOYEE PERSONAL INFO
        // ============================================
        employee_name: 'Employee Name',
        employee_id: 'Employee ID / Emp Code',
        father_name: 'Father Name',
        gender: 'Gender',
        date_of_birth: 'Date of Birth',
        date_of_joining: 'Date of Joining',
        date_of_retirement: 'Date of Retirement',
        gratuity_start_date: 'Gratuity Start Date',

        // ============================================
        // IDENTITY & ACCOUNT NUMBERS
        // ============================================
        pan_number: 'PAN Number',
        aadhaar_number: 'Aadhaar Number',
        uan_number: 'UAN Number',
        pf_number: 'PF / GPF Number',
        esi_number: 'ESI Number',
        eps_number: 'EPS Number',
        pran_number: 'PRAN Number (NPS)',
        saf_number: 'SAF Number',

        // ============================================
        // COMPANY INFO
        // ============================================
        company_name: 'Company Name',
        designation: 'Designation',
        department: 'Department',
        location: 'Work Location',
        work_location: 'Work Location',
        cost_centre: 'Cost Centre',
        band_grade: 'Band / Grade / Level',
        per_sub_area: 'Per Sub Area',

        // ============================================
        // BANK INFO
        // ============================================
        bank_name: 'Bank Name',
        bank_account_number: 'Bank Account Number',
        bank_account_last4: 'Bank Account (Last 4)',

        // ============================================
        // PAY PERIOD & ATTENDANCE
        // ============================================
        pay_period: 'Pay Period',
        payable_days: 'Payable Days',
        arrear_days: 'Arrear Days',
        lop_days: 'LOP Days',
        net_days_worked: 'Net Days Worked',
        tax_regime: 'Tax Regime',

        // ============================================
        // EARNINGS - BASIC
        // ============================================
        basic_salary: 'Basic Salary',
        hra: 'HRA (House Rent Allowance)',
        da: 'DA (Dearness Allowance)',
        fixed_da: 'Fixed DA',
        variable_da: 'Variable DA',
        da_on_tpa: 'DA on TPA',
        special_allowance: 'Special Allowance',
        executive_allowance: 'Executive Allowance',

        // ============================================
        // EARNINGS - ALLOWANCES
        // ============================================
        conveyance: 'Conveyance',
        conveyance_allowance: 'Conveyance Allowance',
        transport_allowance: 'Transport Allowance',
        tpa: 'TPA (Transport Allowance)',
        medical_allowance: 'Medical Allowance',
        lta: 'LTA (Leave Travel Allowance)',
        shift_allowance: 'Shift Allowance',
        washing_allowance: 'Washing Allowance',
        food_allowance: 'Food Allowance',
        telephone_allowance: 'Telephone Allowance',
        education_allowance: 'Education Allowance',
        uniform_allowance: 'Uniform Allowance',
        city_compensatory_allowance: 'City Compensatory Allowance',
        overtime_allowance: 'Overtime Allowance',
        incentive: 'Incentive',
        bonus: 'Bonus',
        annual_bonus: 'Annual Bonus',
        arrears: 'Arrears',
        reimbursement: 'Reimbursement',
        other_allowances: 'Other Allowances',

        // ============================================
        // GROSS & TOTALS
        // ============================================
        gross_salary: 'Gross Salary',
        gross_pay: 'Gross Pay',
        total_earnings: 'Total Earnings',

        // ============================================
        // DEDUCTIONS - STATUTORY
        // ============================================
        pf_employee: 'PF (Employee Contribution)',
        pf_employer: 'PF (Employer Contribution)',
        nps_employee: 'NPS (Employee)',
        nps_employer: 'NPS (Employer)',
        professional_tax: 'Professional Tax',
        income_tax: 'Income Tax',
        tds: 'TDS',
        esi: 'ESI',
        esi_employee: 'ESI (Employee)',
        cgegis: 'CGEGIS',
        pli_premium: 'PLI Premium',

        // ============================================
        // DEDUCTIONS - OTHER
        // ============================================
        canteen_deduction: 'Canteen Deduction',
        union_deduction: 'Union Deduction',
        family_benefit_fund: 'Family Benefit Fund',
        labour_welfare_fund: 'Labour Welfare Fund',
        lwf: 'LWF',
        gratuity: 'Gratuity',
        loan_recovery: 'Loan Recovery',
        advance_recovery: 'Advance Recovery',
        insurance_deduction: 'Insurance Deduction',
        mediclaim_recovery: 'Mediclaim Recovery',
        mediclaim_parent: 'Mediclaim (Parent)',
        mediclaim_escp: 'Mediclaim ESCP',
        employee_club: 'Employee Club',
        sec_deposit_advance: 'Security Deposit Advance',
        other_deductions: 'Other Deductions',
        total_deductions: 'Total Deductions',

        // ============================================
        // NET PAY
        // ============================================
        net_salary: 'Net Salary',
        net_pay: 'Net Pay',
        take_home: 'Take Home Pay',
        in_hand_salary: 'In-Hand Salary',

        // ============================================
        // TAX INFO
        // ============================================
        taxable_income: 'Taxable Income',
        gross_income_annual: 'Gross Income (Annual)',
        total_tax: 'Total Tax',
        tax_rebate: 'Tax Rebate',
        surcharge: 'Surcharge',
        education_cess: 'Education Cess',
        net_tax: 'Net Tax',
        tax_deducted_till_date: 'Tax Deducted Till Date',
        tax_to_be_deducted: 'Tax To Be Deducted',
        tax_per_month: 'Tax Per Month',
        standard_deduction: 'Standard Deduction',
        chapter_vi_a_deduction: 'Chapter VI-A Deduction',
        investment_80c: 'Investment u/s 80C',

        // ============================================
        // EMPLOYER CONTRIBUTIONS (RETIRALS)
        // ============================================
        employer_pf_contribution: 'Employer PF Contribution',
        employer_nps_contribution: 'Employer NPS Contribution',
        super_annuation: 'Super Annuation',

        // ============================================
        // LOAN DETAILS
        // ============================================
        loan_emi: 'Loan EMI',
        loan_balance: 'Loan Balance',
    };

    return labels[fieldKey] || fieldKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Format currency value for display (Indian format)
 * @param {number} value - Currency value
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
    if (typeof value !== 'number' || value === 0) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(value);
};

// Default export
const payslipOCRService = {
    processPayslip,
    processMultiplePayslips,
    getServiceInfo,
    testConnectivity,
    applyOCRToFormData,
    formatConfidence,
    getConfidenceSeverity,
    validateFile,
    isPayslipDocument,
    getFieldLabel,
    formatCurrency,
};

export default payslipOCRService;
